<template>
  <main class="sf-page">
    <!-- Hero -->
    <div class="sf-page-hero">
      <div class="sf-page-hero__inner" data-aos="fade-up">
        <p class="sf-page-hero__eyebrow"><span class="sf-page-hero__eyebrow-dot"></span>Notre réseau</p>
        <h1 class="sf-h1">Associations <em>Partenaires</em></h1>
        <p class="sf-page-hero__sub">Découvrez notre réseau d'associations qui œuvrent chaque jour pour un impact social positif.</p>
      </div>
    </div>

    <div v-if="loading" class="sf-loader"><span class="sf-loader__ring"></span></div>

    <template v-else>
      <!-- Filters -->
      <section class="sf-section sf-section--cream" style="padding-bottom:0">
        <div class="sf-container">
          <div class="as-filters-row">
            <div class="sf-filters">
              <button class="sf-pill" :class="{'sf-pill--active': selectedCategory===''}" @click="filterByCategory('')">Toutes</button>
              <button v-for="cat in categories" :key="cat" class="sf-pill" :class="{'sf-pill--active': selectedCategory===cat}" @click="filterByCategory(cat)">{{ cat }}</button>
            </div>
            <div class="sf-search-wrap">
              <Search /><input v-model="searchQuery" @input="debouncedSearch" placeholder="Rechercher…" class="sf-input" style="max-width:240px" />
            </div>
          </div>
        </div>
      </section>

      <!-- Featured -->
      <section v-if="featuredAssociations.length" class="sf-section sf-section--cream" style="padding-bottom:1rem">
        <div class="sf-container">
          <span class="sf-label">À la une</span>
          <div class="as-featured-grid">
            <div v-for="assoc in featuredAssociations" :key="assoc.id" class="as-featured-card" data-aos="fade-up">
              <div class="as-featured-card__banner">
                <div class="as-featured-card__logo-wrap">
                  <img v-if="assoc.logo" :src="imageUrl+assoc.logo" :alt="assoc.name" />
                  <Users v-else />
                </div>
              </div>
              <div class="as-featured-card__body">
                <h3 class="sf-h3">{{ assoc.name }}</h3>
                <p>{{ assoc.description }}</p>
                <router-link :to="`/association/${assoc.slug}`">
                  <button class="sf-btn sf-btn--outline sf-btn--sm" style="margin-top:1rem;width:100%;justify-content:center">Découvrir <ArrowRight class="sf-btn__icon" /></button>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Grid -->
      <section class="sf-section sf-section--cream-d">
        <div class="sf-container">
          <div v-if="!associations.length" class="sf-empty">
            <Users class="sf-empty__icon" />
            <p class="sf-empty__title">Aucune association trouvée</p>
            <p>Essayez de modifier vos filtres.</p>
          </div>
          <div v-else class="as-grid">
            <div v-for="(assoc, i) in associations" :key="assoc.id" class="sf-card" data-aos="fade-up" :data-aos-delay="i*80">
              <div class="sf-card__media" style="height:180px">
                <img v-if="assoc.cover_image" :src="imageUrl+assoc.cover_image" :alt="assoc.name" loading="lazy" />
                <div v-else class="as-placeholder"><Users /></div>
                <span class="sf-card__cat">{{ assoc.category }}</span>
                <span v-if="assoc.is_featured" class="sf-card__badge">À la une</span>
              </div>
              <div class="sf-card__body">
                <div class="as-card-head">
                  <div class="as-logo" v-if="assoc.logo"><img :src="imageUrl+assoc.logo" :alt="assoc.name" /></div>
                  <div class="as-logo as-logo--text" v-else>{{ getInitials(assoc.name) }}</div>
                  <h3 class="sf-h3" style="font-size:1.2rem">{{ assoc.name }}</h3>
                </div>
                <p class="as-desc">{{ assoc.description }}</p>
                <div style="display:flex;flex-direction:column;gap:.4rem;margin-bottom:1rem">
                  <span class="sf-chip"><MapPin />{{ assoc.location }}</span>
                  <span class="sf-chip"><Calendar />Partenaire depuis {{ assoc.partnership_since }}</span>
                </div>
                <div class="as-stats">
                  <div class="as-stat"><span>{{ assoc.active_projects||0 }}</span><small>Projets actifs</small></div>
                  <div class="as-stat"><span>{{ assoc.members_count||0 }}</span><small>Membres</small></div>
                </div>
                <router-link :to="`/association/${assoc.slug}`" style="display:block;margin-top:1.25rem">
                  <button class="sf-btn sf-btn--outline sf-btn--full">En savoir plus <ArrowRight class="sf-btn__icon" /></button>
                </router-link>
              </div>
            </div>
          </div>

          <!-- Pagination -->
          <div v-if="pagination.last_page>1" class="sf-pagination">
            <button class="sf-page-btn" :disabled="pagination.current_page===1" @click="changePage(pagination.current_page-1)"><ChevronLeft /></button>
            <button v-for="p in displayedPages" :key="p" class="sf-page-btn" :class="{'sf-page-btn--active':pagination.current_page===p}" @click="changePage(p)">{{ p }}</button>
            <button class="sf-page-btn" :disabled="pagination.current_page===pagination.last_page" @click="changePage(pagination.current_page+1)"><ChevronRight /></button>
          </div>
        </div>
      </section>

      <!-- CTA -->
      <section class="sf-section sf-section--cream">
        <div class="sf-container">
          <div class="sf-cta-banner" data-aos="fade-up">
            <p class="sf-cta-banner__title">Rejoignez notre <em>réseau</em></p>
            <p class="sf-cta-banner__sub">Vous êtes une association et vous souhaitez rejoindre notre réseau de partenaires ? Ensemble, multiplions notre impact social !</p>
            <router-link to="/contact"><button class="sf-btn sf-btn--amber">Devenir partenaire <ArrowRight class="sf-btn__icon" /></button></router-link>
          </div>
        </div>
      </section>
    </template>

    <Transition name="toast"><div v-if="toastMessage" class="sf-toast"><CheckCircle />{{ toastMessage }}</div></Transition>
  </main>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { debounce } from 'lodash'
import AOS from 'aos'; import 'aos/dist/aos.css'
import { Users, MapPin, Calendar, ArrowRight, Search, ChevronLeft, ChevronRight, CheckCircle } from 'lucide-vue-next'
export default {
  name:'AssociationsPage',
  components:{ Users, MapPin, Calendar, ArrowRight, Search, ChevronLeft, ChevronRight, CheckCircle },
  data() { return { searchQuery:'', selectedCategory:'', displayedPages:[], toastMessage:'' } },
  computed: {
    ...mapState({ loading:s=>s.loading, associations:s=>s.associations, featuredAssociations:s=>s.featuredAssociations, categories:s=>s.associationCategories, pagination:s=>s.associationsPagination, imageUrl:s=>s.imageUrl })
  },
  watch: {
    'pagination.last_page':{handler(){this.updateDisplayedPages()},immediate:true},
    'pagination.current_page':{handler(){this.updateDisplayedPages()}},
    toastMessage(v){if(v)setTimeout(()=>{this.toastMessage=''},3000)}
  },
  created() { this.debouncedSearch=debounce(this.handleSearch,500); this.loadData() },
  mounted() { AOS.init({once:true,offset:70,easing:'ease-out-cubic',duration:650}) },
  methods: {
    ...mapActions(['fetchPublicAssociations','fetchFeaturedAssociations','fetchAssociationCategories','setAssociationFilter','setAssociationPage']),
    async loadData() { try { await Promise.all([this.fetchPublicAssociations(),this.fetchFeaturedAssociations(),this.fetchAssociationCategories()]) } catch(e){this.toastMessage='Erreur de chargement'} },
    handleSearch(){this.setAssociationFilter({key:'search',value:this.searchQuery});this.setAssociationPage(1);this.fetchPublicAssociations()},
    filterByCategory(c){this.selectedCategory=c;this.setAssociationFilter({key:'category',value:c});this.setAssociationPage(1);this.fetchPublicAssociations()},
    changePage(p){if(p>=1&&p<=this.pagination.last_page){this.setAssociationPage(p);this.fetchPublicAssociations();window.scrollTo({top:0,behavior:'smooth'})}},
    updateDisplayedPages(){const c=this.pagination.current_page,l=this.pagination.last_page,d=2;let pg=[];for(let i=1;i<=l;i++){if(i===1||i===l||(i>=c-d&&i<=c+d))pg.push(i)}this.displayedPages=pg},
    getInitials(n){return n?n.split(' ').map(x=>x[0]).join('').toUpperCase().slice(0,2):'?'}
  }
}
</script>
<style>@import './sf-design.css';</style>
<style scoped>
.as-filters-row{display:flex;align-items:flex-start;justify-content:space-between;flex-wrap:wrap;gap:1rem;margin-bottom:2rem}
.as-featured-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:1.5rem;margin-top:1rem}
.as-featured-card{background:var(--white);border-radius:8px;overflow:hidden;box-shadow:var(--shadow-sm)}
.as-featured-card__banner{height:80px;background:linear-gradient(135deg,var(--forest),var(--forest-l));position:relative}
.as-featured-card__logo-wrap{position:absolute;bottom:-24px;left:1.25rem;width:52px;height:52px;border-radius:8px;background:var(--white);box-shadow:0 4px 12px rgba(0,0,0,.15);display:flex;align-items:center;justify-content:center;overflow:hidden}
.as-featured-card__logo-wrap img{width:38px;height:38px;object-fit:contain}
.as-featured-card__logo-wrap :deep(svg){width:24px;height:24px;color:var(--terra)}
.as-featured-card__body{padding:2rem 1.5rem 1.5rem}
.as-featured-card__body p{font-size:.84rem;color:var(--ink-60);line-height:1.6;margin-top:.5rem;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.as-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(320px,1fr));gap:1.5rem}
.as-placeholder{width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:var(--sage)}
.as-placeholder :deep(svg){width:40px;height:40px}
.as-card-head{display:flex;align-items:center;gap:.75rem;margin-bottom:.75rem}
.as-logo{width:44px;height:44px;border-radius:6px;overflow:hidden;background:var(--cream-d);flex-shrink:0;display:flex;align-items:center;justify-content:center}
.as-logo img{width:100%;height:100%;object-fit:contain}
.as-logo--text{font-size:.9rem;font-weight:600;color:var(--terra)}
.as-desc{font-size:.84rem;color:var(--ink-60);line-height:1.55;margin-bottom:.75rem;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.as-stats{display:grid;grid-template-columns:1fr 1fr;gap:.5rem}
.as-stat{background:var(--cream-d);border-radius:6px;padding:.75rem;text-align:center}
.as-stat span{display:block;font-family:var(--fd);font-size:1.6rem;font-weight:700;color:var(--forest)}
.as-stat small{font-size:.72rem;color:var(--ink-60)}
</style>
